'use client';

/**
 * Data + filter + action state hook for the library catalog page.
 *
 * Module: app/library/use-library
 * Layer:  hook
 *
 * Purpose:
 *     Encapsulates catalog/checkout/assignment loading, the search + type +
 *     category filters, the authentication probe, and the checkout/return
 *     actions so the page component stays thin and every function clears the
 *     CC<=5 / <=60-line gates. Filters are local UI state (the catalog is a
 *     single page; there is no URL-driven deep link requirement yet).
 *
 * AI context:
 *     - Entry points called by: src/app/library/page.tsx (LibraryContent).
 *     - Depends on: lib/library fetch helpers, lib/auth loadAuthState.
 *     - Side effects: network I/O (catalog, checkouts, assignments, mutations).
 *     - Invariants: catalog loads are cancellable; list-fetch failures degrade
 *       to empty arrays (helpers never throw); mutation failures set an error
 *       flag and trigger a refresh so counts stay accurate.
 *     - Replacement cost: LOW.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { loadAuthState } from '@/lib/auth';
import {
  type BookCheckout,
  type BookType,
  type LibraryBook,
  type ReadingAssignment,
  checkoutBook,
  fetchLibraryBooks,
  fetchMyAssignments,
  fetchMyCheckouts,
  returnBook,
} from '@/lib/library';

/** The active catalog filters. */
export interface LibraryFilters {
  readonly search: string;
  readonly bookType: BookType | 'all';
  readonly category: string | 'all';
}

/** Which mutation, if any, the most recent failure came from. */
export type LibraryActionError = 'checkout' | 'return' | null;

/** Everything the catalog page needs to render and react to user input. */
export interface LibraryController {
  readonly books: readonly LibraryBook[];
  readonly checkouts: readonly BookCheckout[];
  readonly assignments: readonly ReadingAssignment[];
  readonly categories: readonly string[];
  readonly loading: boolean;
  readonly authenticated: boolean;
  readonly draftSearch: string;
  readonly filters: LibraryFilters;
  readonly busyBookId: number | null;
  readonly busyCheckoutId: number | null;
  readonly actionError: LibraryActionError;
  readonly setDraftSearch: (next: string) => void;
  readonly submitSearch: () => void;
  readonly setBookType: (next: BookType | 'all') => void;
  readonly setCategory: (next: string) => void;
  readonly clearFilters: () => void;
  readonly checkout: (bookId: number) => void;
  readonly returnCheckout: (checkoutId: number) => void;
}

/** Translate the UI filter state into the lib query shape. */
function toQuery(filters: LibraryFilters): {
  readonly search?: string;
  readonly bookType?: BookType;
  readonly category?: string;
} {
  return {
    ...(filters.search ? { search: filters.search } : {}),
    ...(filters.bookType === 'all' ? {} : { bookType: filters.bookType }),
    ...(filters.category === 'all' ? {} : { category: filters.category }),
  };
}

/** Loaded catalog + personal lists plus the in-flight flag. */
interface LibraryData {
  readonly books: readonly LibraryBook[];
  readonly checkouts: readonly BookCheckout[];
  readonly assignments: readonly ReadingAssignment[];
  readonly loading: boolean;
}

/**
 * Load the catalog and (when authenticated) the student's lists.
 *
 * @param filters - Active catalog filters.
 * @param authed - Whether the visitor is signed in.
 * @param reloadKey - Bump to force a refetch after a mutation.
 * @returns The loaded data plus a loading flag.
 * @remarks Cancels stale responses on dependency change to avoid races.
 */
function useLibraryData(
  filters: LibraryFilters,
  authed: boolean,
  reloadKey: number,
): LibraryData {
  const [data, setData] = useState<LibraryData>({
    books: [],
    checkouts: [],
    assignments: [],
    loading: true,
  });
  const query = useMemo(() => toQuery(filters), [filters]);

  useEffect(() => {
    const controller = new AbortController();
    const run = async (): Promise<void> => {
      setData((prev) => ({ ...prev, loading: true }));
      const [books, checkouts, assignments] = await Promise.all([
        fetchLibraryBooks(query, controller.signal),
        authed ? fetchMyCheckouts(controller.signal) : Promise.resolve([] as const),
        authed ? fetchMyAssignments(controller.signal) : Promise.resolve([] as const),
      ]);
      if (!controller.signal.aborted) {
        setData({ books, checkouts, assignments, loading: false });
      }
    };
    void run();
    return (): void => {
      controller.abort();
    };
  }, [query, authed, reloadKey]);

  return data;
}

/** Probe the BFF session once on mount to gate the personal sections. */
function useAuthenticated(): boolean {
  const [authed, setAuthed] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    const probe = async (): Promise<void> => {
      const state = await loadAuthState();
      if (!controller.signal.aborted) {
        setAuthed(state.status === 'authenticated');
      }
    };
    void probe();
    return (): void => {
      controller.abort();
    };
  }, []);
  return authed;
}

/** Derive the sorted unique category facet list from the loaded catalog. */
function deriveCategories(books: readonly LibraryBook[]): readonly string[] {
  const seen = new Set<string>();
  for (const book of books) {
    if (book.category) {
      seen.add(book.category);
    }
  }
  return [...seen].toSorted((a, b) => a.localeCompare(b));
}

/** Mutation actions (checkout / return) plus their busy + error state. */
interface LibraryActions {
  readonly busyBookId: number | null;
  readonly busyCheckoutId: number | null;
  readonly actionError: LibraryActionError;
  readonly checkout: (bookId: number) => void;
  readonly returnCheckout: (checkoutId: number) => void;
}

/**
 * Wire the checkout / return mutations to busy + error state and a reload.
 *
 * @param onChanged - Called after a successful mutation to refetch lists.
 * @returns The action handlers plus their in-flight + error flags.
 * @remarks Mutation helpers reject on failure; we catch, flag the error, and
 *   still clear the busy id so the button re-enables.
 */
/**
 * Run one library mutation, threading busy/error/reload side effects.
 *
 * @param mutate - The async mutation (checkout or return).
 * @param onChanged - Refetch callback fired on success.
 * @param setBusy - Setter that clears the relevant busy id in `finally`.
 * @param failure - The error tag to set when the mutation rejects.
 * @returns Resolves once the mutation settles (never rejects).
 */
async function runAction(
  mutate: () => Promise<void>,
  onChanged: () => void,
  setBusy: (id: number | null) => void,
  setError: (e: LibraryActionError) => void,
  failure: LibraryActionError,
): Promise<void> {
  try {
    await mutate();
    onChanged();
  } catch {
    setError(failure);
  } finally {
    setBusy(null);
  }
}

function useLibraryActions(onChanged: () => void): LibraryActions {
  const [busyBookId, setBusyBookId] = useState<number | null>(null);
  const [busyCheckoutId, setBusyCheckoutId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<LibraryActionError>(null);

  const checkout = useCallback(
    (bookId: number): void => {
      setActionError(null);
      setBusyBookId(bookId);
      void runAction(() => checkoutBook(bookId), onChanged, setBusyBookId, setActionError, 'checkout');
    },
    [onChanged],
  );

  const returnCheckout = useCallback(
    (checkoutId: number): void => {
      setActionError(null);
      setBusyCheckoutId(checkoutId);
      void runAction(
        () => returnBook(checkoutId),
        onChanged,
        setBusyCheckoutId,
        setActionError,
        'return',
      );
    },
    [onChanged],
  );

  return { busyBookId, busyCheckoutId, actionError, checkout, returnCheckout };
}

/** Search + type + category filter state and their setters. */
interface FilterState {
  readonly filters: LibraryFilters;
  readonly draftSearch: string;
  readonly setDraftSearch: (next: string) => void;
  readonly submitSearch: () => void;
  readonly setBookType: (next: BookType | 'all') => void;
  readonly setCategory: (next: string) => void;
  readonly clearFilters: () => void;
}

const INITIAL_FILTERS: LibraryFilters = { search: '', bookType: 'all', category: 'all' };

/** Manage the catalog filters and the editable search draft. */
function useFilters(): FilterState {
  const [filters, setFilters] = useState<LibraryFilters>(INITIAL_FILTERS);
  const [draftSearch, setDraftSearch] = useState('');

  const submitSearch = useCallback((): void => {
    setFilters((prev) => ({ ...prev, search: draftSearch.trim() }));
  }, [draftSearch]);
  const setBookType = useCallback((next: BookType | 'all'): void => {
    setFilters((prev) => ({ ...prev, bookType: next }));
  }, []);
  const setCategory = useCallback((next: string): void => {
    setFilters((prev) => ({ ...prev, category: next }));
  }, []);
  const clearFilters = useCallback((): void => {
    setFilters(INITIAL_FILTERS);
    setDraftSearch('');
  }, []);

  return {
    filters,
    draftSearch,
    setDraftSearch,
    submitSearch,
    setBookType,
    setCategory,
    clearFilters,
  };
}

/**
 * Top-level controller for the library catalog page.
 *
 * @returns The {@link LibraryController} consumed by the page components.
 * @remarks Composes the auth probe, filter state, data load, and mutation
 *   actions; a reload counter forces a refetch after each successful action.
 */
export function useLibrary(): LibraryController {
  const authenticated = useAuthenticated();
  const fs = useFilters();
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback((): void => setReloadKey((n) => n + 1), []);
  const data = useLibraryData(fs.filters, authenticated, reloadKey);
  const actions = useLibraryActions(reload);
  const categories = useMemo(() => deriveCategories(data.books), [data.books]);

  return {
    books: data.books,
    checkouts: data.checkouts,
    assignments: data.assignments,
    categories,
    loading: data.loading,
    authenticated,
    draftSearch: fs.draftSearch,
    filters: fs.filters,
    busyBookId: actions.busyBookId,
    busyCheckoutId: actions.busyCheckoutId,
    actionError: actions.actionError,
    setDraftSearch: fs.setDraftSearch,
    submitSearch: fs.submitSearch,
    setBookType: fs.setBookType,
    setCategory: fs.setCategory,
    clearFilters: fs.clearFilters,
    checkout: actions.checkout,
    returnCheckout: actions.returnCheckout,
  };
}
